<template>
  <VCard class="v-form-builder-section" :class="{ 'no-body': !includeDescription }">
    <template #title>
      <div ref="title" class="title-25" :contenteditable="renaming ? (supportsPlaintextEditableContent() ? 'plaintext-only' : 'true') : false" @blur="stopRenaming">
        {{ blueprint.activeSection?.title }}
      </div>
      <VButton v-show="!renaming" :label="i18n.rename" size="small" type="secondary" @click="startRenaming" />
    </template>
    <template #header-right>
      <VSwitch :label="i18n.includeDescription" :model-value="includeDescription" @update:model-value="switchDescription" />
    </template>

    <div v-if="includeDescription">
      <div class="v-form-builder-section__description">{{ i18n.description }}</div>
      <VInput v-model="blueprint.activeSection.description" :placeholder="i18n.enterTheDescription" size="medium" style="margin-bottom: 10px" />
    </div>
  </VCard>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'

import { delay, supportsPlaintextEditableContent } from '../../utils/helpers.ts'
import { i18nFormBuilder as i18n } from '../../utils/i18n.ts'
import VButton from '../VButton.vue'
import VCard from '../VCard.vue'
import VInput from '../VInput.vue'
import VSwitch from '../VSwitch.vue'
import { type Blueprint } from './useBlueprint.ts'

interface Props {
  blueprint: Blueprint
}

const props = defineProps<Props>()

const title = useTemplateRef('title')
const renaming = ref(false)
const includeDescription = ref(!!props.blueprint.activeSection.description)

async function startRenaming() {
  if (!title.value) {
    return
  }
  renaming.value = true
  await delay(0) // Wait for element to become focusable
  title.value?.focus()
  // Put caret at the end of the text
  const range = document.createRange()
  const selection = window.getSelection()
  range.setStart(title.value, title.value.childNodes.length)
  range.collapse(true)
  selection?.removeAllRanges()
  selection?.addRange(range)
}

function stopRenaming() {
  const textContent = title.value?.textContent

  if (textContent && props.blueprint.activeSection) {
    props.blueprint.activeSection.title = textContent
  }

  renaming.value = false
}

function switchDescription(value: boolean) {
  includeDescription.value = value
  if (!value) {
    props.blueprint.activeSection.description = null
  }
}
</script>

<style>
.v-form-builder-section {
  .v-card__header {
    border-top: 10px solid var(--color-primary);
    padding: 10px 20px;
  }

  .v-card__header-right {
    color: var(--color-info);
  }

  .v-card__body {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .title-25 {
    outline: none;
  }
  .v-btn {
    margin-left: 20px;
  }
}

.v-form-builder-section__description {
  font-weight: 500;
  margin-bottom: 10px;
}

.no-body {
  .v-card__body {
    display: none;
  }
}
</style>
